import { SuburbFillResult } from '../../../../services/suburb-areas.service';

/**
 * How a suburb's (or a sub-region's) facts read in the side panel (#256, #253, #260): the search gate, the crawl's
 * counts, the change log, the fill preview's skipped suburbs. Pure, tested in suburb-facts.spec.ts. The suburb detail
 * comes from the server untyped (SuburbDataService.detail); these read it defensively.
 */

export interface Count {
  key: string;
  n: number;
}

/** The website build's gate report for a page (#253): its funeral-services gate, and how the other verbs fare. */
export interface GateSummary {
  main: any | null;
  others: { pass: number; total: number };
  reportedAt: string | null;
}

/** How a name is looked up for the change log and the fill preview. */
export type NameOf = (id: string | null | undefined) => string;

/** How many of each kind and type the Data tab shows. */
const TOP_KINDS = 12;
const TOP_TYPES = 10;
/** How many skipped suburbs the fill preview names before "and N more". */
const SKIPPED_SHOWN = 12;
const MAIN_VERB = 'funeral-services';

/** A link only when it is http(s): crawl data is untrusted. */
export function safeUrl(u: unknown): string | null {
  return typeof u === 'string' && /^https?:\/\//i.test(u) ? u : null;
}

export function gateOf(d: any): GateSummary | null {
  const rows: any[] = Array.isArray(d?.gates) ? d.gates : [];
  if (!rows.length) return null;
  const main = rows.find((r) => r.verb === MAIN_VERB) ?? null;
  const others = rows.filter((r) => r.verb !== MAIN_VERB);
  return {
    main,
    others: { pass: others.filter((r) => r.pass).length, total: others.length },
    reportedAt: (main ?? rows[0]).reportedAt ?? null,
  };
}

/** The search gate's G1 (places nearby): where the nearest places are. */
export function nearbyNote(g1: any): string {
  if (g1?.inside) return ', one in the suburb';
  return g1?.uniqueNearest ? ', nearest places of its own' : ', none in the suburb';
}

/** The search gate's G2 (the lead): where the page's lead comes from. */
export function leadSource(g2: any): string {
  if (g2?.source === 'intro') return 'your intro';
  return g2?.source === 'data' ? 'written from the places' : 'the shared sentence';
}

export function pct(v: number | null | undefined): string {
  return typeof v === 'number' ? `${Math.round(v * 100)}%` : '?';
}

/** "Population 12,345 · postcode 2031", whichever the crawl has. */
export function crawlFacts(c: any): string {
  const population = c?.population
    ? `Population ${Number(c.population).toLocaleString('en-AU')}`
    : '';
  const postcode = c?.postcode ? `postcode ${c.postcode}` : '';
  return [population, postcode].filter(Boolean).join(' · ');
}

/** One line of a suburb's change log: a region move, a page flag, or joining or leaving a sub-region. */
export function changeText(c: any, regionName: NameOf, subRegionName: NameOf): string {
  if (c.field === 'subregion')
    return `${c.to === '1' ? 'joined' : 'left'} ${subRegionName(c.subRegionId) || 'a sub-region since deleted'}`;
  const side = (x: string | null) => {
    if (c.field === 'covered') return x === '1' ? 'has a page' : 'no page';
    return x ? regionName(x) : 'unassigned';
  };
  return `${side(c.from)} → ${side(c.to)}`;
}

/** The largest counts first, at most `n`, zeros left out. */
export function topCounts(o: Record<string, number> | null | undefined, n: number): Count[] {
  return Object.entries(o ?? {})
    .map(([key, v]) => ({ key, n: +v }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n)
    .slice(0, n);
}

/** Venues per kind, the most common first. */
export function countKinds(venues: Array<{ kind: string }> = []): Count[] {
  const m = new Map<string, number>();
  for (const v of venues) m.set(v.kind, (m.get(v.kind) ?? 0) + 1);
  return [...m].map(([key, n]) => ({ key, n })).sort((a, b) => b.n - a.n);
}

/** The suburb detail as the panel shows it: the server's, plus its venue kinds and the crawl's top kinds and types. */
export function detailView(d: any): any {
  return {
    ...d,
    venueKinds: countKinds(d.venues),
    topKinds: topCounts(d.crawl?.byKind, TOP_KINDS),
    types: topCounts(d.crawl?.byType, TOP_TYPES),
  };
}

/** The fill preview's skipped suburbs, named with why (another region, a page elsewhere, or unassigned while that's off). */
export function skippedText(fp: SuburbFillResult, regionName: NameOf): string {
  const why = (x: SuburbFillResult['skipped'][number]) => {
    if (x.regionId) return `in ${regionName(x.regionId)}`;
    return x.reason ? 'its page is elsewhere' : 'in no region';
  };
  const shown = fp.skipped.slice(0, SKIPPED_SHOWN).map((x) => `${x.name} (${why(x)})`);
  const more =
    fp.summary.skipped > shown.length ? ` and ${fp.summary.skipped - shown.length} more` : '';
  return shown.join(', ') + more;
}
